import { fetchBaseQuery } from "@reduxjs/toolkit/dist/query";
import { getApiHostUrl, getDefaultHeaders } from "../helpers/apiHelpers";
import { getKindeToken } from "./kindeTokenProvider";
import { accountIdSelector } from "./slices/account/selectors";
import { operatorIdSelector, defaultCountrySelector } from "./slices/operator/selectors";

// Form data related requests skips header setting
// https://github.com/reduxjs/redux-toolkit/issues/2287#issuecomment-1111213377
const FORMDATA_ENDPOINTS = [
  "uploadAsset",
  "addAccreditation",
  "editAccreditation",
  "updateAvatar",
  "addBookingOfferDocument",
  "addJobAttachment",
  "addAvatar",
  "addVehicleDocument",
  "addDriverDocument",
  "addDriverIncidentDocument",
  "uploadDocument",
];

/**
 * Base query that uses Kinde access token for authentication
 * Token is fetched on-demand for each request (not stored in Redux)
 */
export const baseQuery = fetchBaseQuery({
  baseUrl: getApiHostUrl(),
  prepareHeaders: async (headers, { endpoint }) => {
    // Get Kinde token for each request
    const token = await getKindeToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // Set default headers for non-form-data requests
    if (!FORMDATA_ENDPOINTS.includes(endpoint)) {
      headers = getDefaultHeaders(headers);
    }
    return headers;
  },
});

/**
 * Base query with provider country in URL
 * Example: /v3/provider/{operatorId}/config/{country}/...
 */
export const baseQueryWithProviderCountry: (endpoint?: string) => typeof baseQuery =
  (endpoint = "config") =>
  async (args, api, extraOptions) => {
    const country = defaultCountrySelector(api.getState());
    const operatorId = operatorIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/provider/${operatorId}${endpoint ? `/${endpoint}` : ""}/${country}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query with provider in URL
 * Example: /v3/provider/{operatorId}/...
 */
export const baseQueryWithProvider: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const operatorId = operatorIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/provider/${operatorId}/${endpoint}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query with endpoint first, then provider in URL
 * Example: /v3/{endpoint}/{operatorId}/...
 */
export const baseQueryWithProviderEndpointFirst: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const operatorId = operatorIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/${endpoint}/${operatorId}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query without provider in URL
 * Example: /v3/{endpoint}/...
 */
export const baseQueryWithoutProvider: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/${endpoint}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query with account management in URL
 * Example: /v3/account-management/{accountId}/...
 */
export const baseQueryWithAccount: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const accountId = accountIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/account-management/${accountId}/${endpoint}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };
